import { useAuth } from "contexts/AuthContext";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppstoreOutlined,
  BankOutlined,
  CloseOutlined,
  CreditCardOutlined,
  DollarOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { buildOlitaskNav } from "../../nav/navData";
import paths from "../../nav/paths";
import { useWorkspace } from "contexts/WorkspaceContext";
import { hasAdminAccess, membershipUserId } from "workspaces/workspaceUtils";

const NAV_ICONS = {
  profile:        "id-card",
  "account-profile": "id-card",
  "job-search": "search",
  "talent-search": "search",
  "job-posts": "briefcase",
  applications: "list-alt",
  interviews: "calendar-check",
  "create-job": "plus-square",
  "professional-profiles": "user-tie",
  "background-checks": "shield-alt",
  organizations: "building",
  invitations: "envelope-open-text",
  reports: "chart-line",
  website: "globe",
  "start-professional-verification": "user-shield",
  chat: "comments",
  inbox: "envelope",
  "chat-space": "comments",
  "email-messages": "envelope",
  "access-control": "shield-alt",
  workspaces: "th-large",
  wallet: "credit-card",
  partners: "handshake",
  settings: "cog",
  "settings-workspaces": "th-large",
  "settings-change-password": "key",
  "settings-security-questions": "shield-alt",
  "settings-verify-email": "envelope",
  "settings-verify-phone": "phone-alt",
  "settings-app-details": "info-circle",
  "settings-supported-locations": "map-marker-alt",
  "settings-delete-account": "trash-alt",
  "settings-notifications": "bell",
  "settings-language": "globe",
  "settings-theme": "palette",
  "settings-data-storage": "archive",
  feedback: "comment",
  "help-support": "headset",
  legal: "file-contract",
  tasks: "tasks",
  "create-request": "plus-square",
  "helper-profile": "handshake",
  "requester-profile": "user",
  "start-helper-verification": "user-shield",
  earnings: "file-invoice-dollar",
  credits: "gift",
  donations: "heart",
};

export default function Sidebar({ mobileSbOpen, onCloseMobile }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const { selectedWorkspace, selectedWorkspaceUse } = useWorkspace();
  const userId = user?.id || user?.userId || "";
  const selectedMembership = (selectedWorkspace?.users || []).find(
    (membership) => membershipUserId(membership) === userId,
  );
  const canManageWorkspace =
    hasAdminAccess(selectedMembership) ||
    hasAdminAccess(selectedWorkspace) ||
    String(selectedWorkspace?.accountType || "").toUpperCase() === "PERSONAL";
  const { topNav, bottomNav } = buildOlitaskNav({
    selectedWorkspace,
    selectedWorkspaceUse,
    canManageWorkspace,
    userId,
  });

  const isAct = (to) => pathname === to || pathname.startsWith(`${to}/`);
  const isExternalUrl = (to) => /^https?:\/\//i.test(String(to || ""));
  const itemIsActive = (item) => {
    const to = String(item.to || "");
    if (to && !isExternalUrl(to)) {
      const [targetPath, targetHash = ""] = to.split("#");
      if (targetHash) {
        return pathname === targetPath && hash === `#${targetHash}`;
      }
      if (isAct(targetPath)) return true;
    }
    return (item.children || []).some(itemIsActive);
  };

  const iconNode = (item) => (
    <span className="pm-ni-ico">
      {item.id === "workspaces" || item.id === "settings-workspaces" ? (
        <AppstoreOutlined />
      ) : item.id === "workspace-context" ? (
        <AppstoreOutlined />
      ) : item.id === "organization" ? (
        <BankOutlined />
      ) : item.id === "subscriptions" ? (
        <CreditCardOutlined />
      ) : item.id === "payment-methods" ? (
        <CreditCardOutlined />
      ) : item.id === "charges" ? (
        <DollarOutlined />
      ) : (
        <FontAwesomeIcon icon={NAV_ICONS[item.id] || "circle"} />
      )}
    </span>
  );

  const handleNavigate = (item) => {
    if (item.disabled || !item.to) return;
    if (item.children?.length) {
      setExpandedItems((current) => ({
        ...current,
        [item.id]: !current[item.id],
      }));
    }
    if (collapsed && item.children?.length) {
      setCollapsed(false);
      return;
    }
    if (item.children?.length) return;
    if (isExternalUrl(item.to)) {
      window.open(item.to, "_blank", "noopener,noreferrer");
      onCloseMobile?.();
      return;
    }
    navigate(item.to);
    onCloseMobile?.();
  };

  const renderSubItem = (item, depth = 0) => {
    const children = item.children || [];
    const className = depth > 0 ? "pm-ss-ni" : "pm-sub-ni";

    return (
      <div key={item.id}>
        <div
          className={`${className}${item.disabled ? " disabled" : ""}${itemIsActive(item) ? " act" : ""}`}
          onClick={() => handleNavigate(item)}
          aria-disabled={item.disabled ? "true" : undefined}
        >
          <span className="pm-node-left">
            <span className="pm-node-ico">
              {item.id === "settings-workspaces" ? (
                <AppstoreOutlined />
              ) : (
                <FontAwesomeIcon icon={NAV_ICONS[item.id] || "circle"} />
              )}
            </span>
            <span>{item.label}</span>
          </span>
          {children.length ? (
            <span className="pm-node-chev">
              <FontAwesomeIcon icon="chevron-down" />
            </span>
          ) : null}
        </div>
        {children.length ? (
          <div className={depth > 0 ? "pm-sub-sub" : "pm-sub-sub"}>
            {children.map((child) => renderSubItem(child, depth + 1))}
          </div>
        ) : null}
      </div>
    );
  };

  const renderNavItem = (item) => {
    if (item.section) {
      return (
        <div key={item.id} className="pm-glbl">
          {item.label}
        </div>
      );
    }

    const children = item.children || [];
    const childrenExpanded = Boolean(expandedItems[item.id]);

    return (
      <div key={item.id}>
        <div
          className={`pm-ni${item.disabled ? " disabled" : ""}${itemIsActive(item) ? " act" : ""}`}
          onClick={() => handleNavigate(item)}
          aria-disabled={item.disabled ? "true" : undefined}
        >
          {iconNode(item)}
          <span className="pm-ni-lbl">{item.label}</span>
          {item.badge && <span className="pm-badge">{item.badge}</span>}
          {children.length ? (
            <span className={`pm-node-chev${childrenExpanded ? " open" : ""}`}>
              <FontAwesomeIcon icon="chevron-down" />
            </span>
          ) : null}
          <span className="pm-tip">{item.label}</span>
        </div>
        {children.length && childrenExpanded ? (
          <div className="pm-sub-tree">
            {children.map((child) => renderSubItem(child))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <aside
      className={`pm-sb${collapsed ? " col" : ""}${mobileSbOpen ? " mobile-open" : ""}`}
      style={{
        flex:     collapsed ? "0 0 44px" : "0 0 256px",
        width:    collapsed ? 44 : 256,
        minWidth: collapsed ? 44 : 256,
        maxWidth: collapsed ? 44 : 256,
      }}
    >
      <div className="pm-sb-head">
        <button
          type="button"
          className="pm-logo-wrap"
          onClick={() => navigate(paths.pmDashboard)}
          aria-label="Go to home"
        >
          <img src="/logo.png" alt="Olitask" className="pm-logo-img" />
        </button>
        <button
          type="button"
          className="pm-sb-toggle"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <MenuOutlined />
        </button>
        <button
          type="button"
          className="pm-sb-close"
          onClick={onCloseMobile}
          aria-label="Close menu"
        >
          <CloseOutlined />
        </button>
      </div>

      <nav className="pm-nav">
        {topNav.map(renderNavItem)}

        <div className="pm-nav-divider" />

        {bottomNav.map(renderNavItem)}
      </nav>

      <div className="pm-sb-foot">
        <div className="pm-foot-title">Account</div>
        <div
          className="pm-foot-ni pm-foot-danger"
          onClick={() => logout(false, paths.landing)}
        >
          <span style={{ fontSize: 14 }}>
            <FontAwesomeIcon icon="right-from-bracket" />
          </span>
          <span className="pm-foot-text">Sign Out</span>
        </div>
      </div>
    </aside>
  );
}
